import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChevronDownIcon,
  FolderIcon,
  InfoIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import AuthPointsSelector from '@/components/organisms/Toolkit/ToolkitAuth/shared/AuthPointsSelector';
import {
  questionTemplates,
  QuestionTemplate,
} from '@/components/organisms/Toolkit/ToolkitAuth/shared/AuthQuestionTemplates';
import { ToolkitEditor } from '@/components/organisms/Toolkit/ToolkitEditor/ToolkitEditor';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Autocomplete, AutocompleteOption } from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import useDialogs from '@/hooks/useDialogs';
import useWebhooks from '@/hooks/useWebhooks';
import { cn } from '@/lib/utils';
import { popModal, pushModal } from '@/redux/modals/actions';
import { selectBrainId } from '@/redux/session/selectors';
import {
  AuthConfig,
  AuthQuestion,
  AuthQuestionType,
  AuthQuestionValidation,
  AuthStep,
  AuthQuestionValidationMethod,
} from '@/shared/types/node';
import { addDollarSign, removeDollarSign } from '@/util/util';
import { LENGTH_XL } from '@/util/validation/constants';
import {
  PARTIAL_MATCH_INCOMPATIBLE_TYPES,
  PARTIAL_MATCH_METHODS,
} from '@/util/validation/schemas/auth-constants';

import { AuthPageStepPanelProps } from '../types';
import useAuthPageValidation from '../useAuthPageValidation';
import AuthPagePanelWrapper from './AuthPagePanelWrapper';

const MAX_QUESTIONS = 10;
const NONE_VALUE = '__none__';

const QUESTION_TYPE_OPTIONS: { value: AuthQuestionType; labelKey: string }[] = [
  { value: 'text', labelKey: 'dialog.auth.question_type_text' },
  { value: 'date', labelKey: 'dialog.auth.question_type_date' },
  { value: 'integer', labelKey: 'dialog.auth.question_type_integer' },
  { value: 'number', labelKey: 'dialog.auth.question_type_number' },
  { value: 'phone', labelKey: 'dialog.auth.question_type_phone' },
  { value: 'email', labelKey: 'dialog.auth.question_type_email' },
  { value: 'alphanumeric', labelKey: 'dialog.auth.question_type_alphanumeric' },
];

const VALIDATION_METHOD_LABEL_KEYS: Record<AuthQuestionValidationMethod, string> = {
  exact_match: 'dialog.auth.validation_exact_match',
  fuzzy_match: 'dialog.auth.validation_fuzzy_match',
  contains: 'dialog.auth.validation_contains',
  starts_with: 'dialog.auth.validation_starts_with',
  ends_with: 'dialog.auth.validation_ends_with',
  guideline: 'dialog.auth.validation_guideline',
  webhook: 'dialog.auth.validation_webhook',
};

const OPERATOR_METHODS: AuthQuestionValidationMethod[] = [
  'exact_match',
  'fuzzy_match',
  'contains',
  'starts_with',
  'ends_with',
];

// Every operator method validates against a ground-truth context variable.
const GROUND_TRUTH_METHODS = new Set<AuthQuestionValidationMethod>(OPERATOR_METHODS);

// Switching between methods rebuilds the validation object so no stale
// method-specific field is sent. Fields that mean the same thing under the new
// method carry over: the ground-truth variable between any two operator
// methods, the match size between partial-match methods. Switching to
// guideline/webhook (or back) starts clean.
const buildValidationForMethod = (
  prev: AuthQuestionValidation,
  method: AuthQuestionValidationMethod,
  webhookId?: string
): AuthQuestionValidation => ({
  method,
  ...(webhookId ? { webhook_id: webhookId } : {}),
  ...(GROUND_TRUTH_METHODS.has(method) && GROUND_TRUTH_METHODS.has(prev.method) && prev.ground_truth
    ? { ground_truth: prev.ground_truth }
    : {}),
  ...(PARTIAL_MATCH_METHODS.has(method) &&
  PARTIAL_MATCH_METHODS.has(prev.method) &&
  prev.match_size !== undefined
    ? { match_size: prev.match_size }
    : {}),
});

const isMethodCompatibleWithType = (
  method: AuthQuestionValidationMethod,
  type?: AuthQuestionType
) => !PARTIAL_MATCH_METHODS.has(method) || !type || !PARTIAL_MATCH_INCOMPATIBLE_TYPES.has(type);

interface ValidationMethodDropdownProps {
  value: AuthQuestionValidationMethod;
  webhookId?: string;
  webhooks: { webhook_id: string; name: string }[];
  questionType?: AuthQuestionType;
  onChange: (method: AuthQuestionValidationMethod, webhookId?: string) => void;
}

const ValidationMethodDropdown = ({
  value,
  webhookId,
  webhooks,
  questionType,
  onChange,
}: ValidationMethodDropdownProps) => {
  const { t } = useTranslation();

  const getDisplayLabel = () => {
    if (value === 'webhook' && webhookId) {
      const wh = webhooks.find((w) => w.webhook_id === webhookId);
      if (wh) {
        return wh.name;
      }
    }
    return t(VALIDATION_METHOD_LABEL_KEYS[value]);
  };

  // Substring/prefix/suffix operators only make sense for text-shaped answers.
  // Hide them when the question type is date/number/phone — matches the
  // schema-level rejection in `auth-schemas.ts`.
  const visibleOperatorMethods = OPERATOR_METHODS.filter((m) =>
    isMethodCompatibleWithType(m, questionType)
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline-neutral" className="w-full justify-between font-normal">
          {getDisplayLabel()}
          <ChevronDownIcon size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>{t('dialog.auth.validation_operator')}</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {visibleOperatorMethods.map((method) => (
              <DropdownMenuItem key={method} onClick={() => onChange(method)}>
                {t(VALIDATION_METHOD_LABEL_KEYS[method])}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onClick={() => onChange('guideline')}>
          {t(VALIDATION_METHOD_LABEL_KEYS.guideline)}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>{t(VALIDATION_METHOD_LABEL_KEYS.webhook)}</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {webhooks.length === 0 ? (
              <DropdownMenuItem disabled>
                {t('dialog.auth.validation_webhook_empty')}
              </DropdownMenuItem>
            ) : (
              webhooks.map((wh) => (
                <DropdownMenuItem
                  key={wh.webhook_id}
                  onClick={() => onChange('webhook', wh.webhook_id)}
                >
                  {wh.name}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const createQuestion = (
  question: string,
  saveAs: string,
  type?: AuthQuestionType,
  groundTruth?: string
): AuthQuestion => ({
  question_id: uuidv4(),
  question,
  save_as: saveAs,
  type,
  points: 1,
  validation: { method: 'exact_match', ground_truth: groundTruth ?? addDollarSign(saveAs) },
});

// Keep the success threshold in sync with the number of questions for the
// common "all questions required" setup: when the threshold currently matches
// the question count, it tracks the new count as questions are added. A
// threshold tuned to something else (alternative auth scenarios) is left as-is.
const syncSuccessThreshold = (
  currentThreshold: number | undefined,
  prevQuestionCount: number,
  nextQuestionCount: number
): number => {
  const threshold = currentThreshold ?? 1;
  return threshold === prevQuestionCount ? nextQuestionCount : threshold;
};

const AuthPageStepPanel = ({
  step,
  authConfig,
  onUpdate,
  onClose,
  showErrors = false,
}: AuthPageStepPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { authValidationWebhooks, authPreEnterWebhooks } = useWebhooks(brainId);
  const { getContextVariables } = useDialogs(brainId);
  const { contextVariables } = useBrainVariables();

  const stepFieldKey = `${step}_step` as const;
  const stepConfig: AuthStep | undefined = authConfig[stepFieldKey];

  const { getFieldError } = useAuthPageValidation(authConfig, showErrors);

  const [searchQuery, setSearchQuery] = useState('');
  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [removedTemplates, setRemovedTemplates] = useState<Set<string>>(new Set());
  const pendingTemplatesRef = useRef<Set<string>>(new Set());
  const pendingFocusQuestionIdRef = useRef<string | null>(null);

  const questions = useMemo(() => stepConfig?.questions ?? [], [stepConfig?.questions]);
  const canAddMore = questions.length < MAX_QUESTIONS;

  // TryIt deep-link: when ?question=<save_as> is present and matches a question
  // in this panel, scroll to its card and briefly highlight it. The
  // useAuthDeepLink hook on the Dialog page strips action/step from the URL;
  // we strip `question` once we've handled it.
  const [searchParams, setSearchParams] = useSearchParams();
  const handledQuestionRef = useRef<string | null>(null);
  useEffect(() => {
    const questionParam = searchParams.get('question');
    if (!questionParam || handledQuestionRef.current === questionParam) {
      return;
    }
    const target = questions.find((q) => q.save_as === questionParam);
    if (!target) {
      return;
    }
    handledQuestionRef.current = questionParam;
    const scrollId = window.setTimeout(() => {
      const node = document.querySelector<HTMLElement>(
        `[data-question-save-as="${CSS.escape(questionParam)}"]`
      );
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const classes = ['ring-2', 'ring-primary', 'ring-offset-2'];
        node.classList.add(...classes);
        window.setTimeout(() => node.classList.remove(...classes), 2000);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('question');
      setSearchParams(next, { replace: true });
    }, 50);
    return () => window.clearTimeout(scrollId);
  }, [questions, searchParams, setSearchParams]);

  const saveAsOptions = useMemo((): AutocompleteOption[] => {
    const contextVariables = getContextVariables().filter(
      (variable) => variable?.value !== '$tags'
    );
    return contextVariables.map((variable) => ({
      value: variable.value,
      label: variable.label,
      category: t(`auto_complete.group_labels.${variable.type}`),
    }));
  }, [getContextVariables, t]);

  const updateStepConfig = useCallback(
    (updates: Partial<AuthStep>) => {
      onUpdate({ [stepFieldKey]: { ...stepConfig, ...updates } } as Partial<AuthConfig>);
    },
    [onUpdate, stepConfig, stepFieldKey]
  );

  const updateQuestions = useCallback(
    (newQuestions: AuthQuestion[]) => {
      updateStepConfig({ questions: newQuestions });
    },
    [updateStepConfig]
  );

  const handleAddCustomQuestion = useCallback(() => {
    if (!canAddMore) {
      return;
    }
    const newQ = createQuestion('', 'variable_name', undefined, '');
    pendingFocusQuestionIdRef.current = newQ.question_id;
    updateStepConfig({
      questions: [...questions, newQ],
      enabled: true,
      failure: stepConfig?.failure ?? {},
      success_points_threshold: syncSuccessThreshold(
        stepConfig?.success_points_threshold,
        questions.length,
        questions.length + 1
      ),
    });
  }, [canAddMore, questions, stepConfig, updateStepConfig]);

  const commitTemplateChanges = useCallback(
    (toCommit: Set<string>, toRemove: Set<string>) => {
      if (toCommit.size === 0 && toRemove.size === 0) {
        return;
      }

      const saveAsToRemove = new Set<string>();
      if (toRemove.size > 0) {
        for (const category of questionTemplates) {
          for (const tmpl of category.templates) {
            if (toRemove.has(tmpl.labelKey)) {
              saveAsToRemove.add(tmpl.defaultSaveAs);
            }
          }
        }
      }

      let updatedQuestions =
        saveAsToRemove.size > 0
          ? questions.filter((q) => !saveAsToRemove.has(q.save_as))
          : [...questions];

      if (toCommit.size > 0) {
        const templatesToAdd: QuestionTemplate[] = [];
        for (const category of questionTemplates) {
          for (const tmpl of category.templates) {
            if (toCommit.has(tmpl.labelKey)) {
              templatesToAdd.push(tmpl);
            }
          }
        }

        const remaining = MAX_QUESTIONS - updatedQuestions.length;
        const toAdd = templatesToAdd.slice(0, remaining);
        const newQuestions = toAdd.map((tmpl) =>
          createQuestion(t(tmpl.labelKey), tmpl.defaultSaveAs, tmpl.type)
        );
        updatedQuestions = [...updatedQuestions, ...newQuestions];
      }

      updateStepConfig({
        questions: updatedQuestions,
        enabled: true,
        failure: stepConfig?.failure ?? {},
        success_points_threshold: syncSuccessThreshold(
          stepConfig?.success_points_threshold,
          questions.length,
          updatedQuestions.length
        ),
      });
    },
    [questions, stepConfig, updateStepConfig, t]
  );

  const handleTemplatePopoverChange = useCallback(
    (open: boolean) => {
      setTemplatePopoverOpen(open);
      if (!open) {
        commitTemplateChanges(pendingTemplatesRef.current, removedTemplates);
        setSelectedTemplates(new Set());
        setRemovedTemplates(new Set());
        pendingTemplatesRef.current = new Set();
        setSearchQuery('');
      }
    },
    [commitTemplateChanges, removedTemplates]
  );

  const handleDeleteQuestion = useCallback(
    (questionId: string, idx: number) => {
      dispatch(
        pushModal(MODAL_DELETE, {
          subtitle: t('dialog.auth.question_delete_confirm', { 0: idx + 1 }),
          confirm: false,
          onDelete: () => {
            updateQuestions(questions.filter((q) => q.question_id !== questionId));
            dispatch(popModal());
          },
          secondaryButtonText: t('common.cancel'),
        })
      );
    },
    [dispatch, questions, t, updateQuestions]
  );

  const handleUpdateQuestion = useCallback(
    (questionId: string, updates: Partial<AuthQuestion>) => {
      updateQuestions(
        questions.map((q) => (q.question_id === questionId ? { ...q, ...updates } : q))
      );
    },
    [questions, updateQuestions]
  );

  const toggleTemplate = useCallback((labelKey: string) => {
    setSelectedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(labelKey)) {
        next.delete(labelKey);
      } else {
        next.add(labelKey);
      }
      pendingTemplatesRef.current = next;
      return next;
    });
  }, []);

  const stepTitle =
    step === 'identification'
      ? t('dialog.auth.step_identification_title')
      : t('dialog.auth.step_verification_title');

  const existingTemplateLabelKeys = useMemo(() => {
    const existingSaveAs = new Set(questions.map((q) => q.save_as));
    const keys = new Set<string>();
    for (const cat of questionTemplates) {
      for (const tmpl of cat.templates) {
        if (existingSaveAs.has(tmpl.defaultSaveAs)) {
          keys.add(tmpl.labelKey);
        }
      }
    }
    return keys;
  }, [questions]);

  const filteredTemplates = useMemo(
    () =>
      questionTemplates
        .map((cat) => ({
          ...cat,
          templates: cat.templates.filter((tmpl) =>
            searchQuery
              ? t(tmpl.shortLabelKey).toLowerCase().includes(searchQuery.toLowerCase())
              : true
          ),
        }))
        .filter((cat) => cat.templates.length > 0),
    [searchQuery, t]
  );

  const questionsError = getFieldError(`${step}-questions`);
  const hasQuestions = questions.length > 0;

  const renderQuestionForm = (q: AuthQuestion, idx: number) => {
    const questionError = getFieldError(`${step}-q-${idx}-question`);
    const saveAsError = getFieldError(`${step}-q-${idx}-save_as`);
    const typeError = getFieldError(`${step}-q-${idx}-type`);
    const validationError = getFieldError(`${step}-q-${idx}-validation`);

    // data-question-save-as is the deep-link target for AuthenticationPage's
    // scroll + highlight handler (e.g. from TryIt's debug steps).
    return (
      <div className="space-y-4 rounded-md transition-shadow" data-question-save-as={q.save_as}>
        {/* Title + trash */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            {t('dialog.auth.question_label', { 0: idx + 1 })}
          </h3>
          <InfoTooltip content={t('dialog.auth.question_delete', { 0: idx + 1 })}>
            <Button
              className="hover:text-destructive hover:[&_svg]:text-destructive"
              variant="ghost-neutral"
              size="icon-sm"
              onClick={() => handleDeleteQuestion(q.question_id, idx)}
              aria-label={t('dialog.auth.question_delete', { 0: idx + 1 })}
            >
              <Trash2Icon size={14} />
            </Button>
          </InfoTooltip>
        </div>

        {/* Question text */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t('dialog.auth.question_text_label')}</Label>
          <div
            ref={(el) => {
              if (el && pendingFocusQuestionIdRef.current === q.question_id) {
                const editable = el.querySelector<HTMLElement>('[contenteditable="true"]');
                if (editable) {
                  editable.focus();
                  pendingFocusQuestionIdRef.current = null;
                }
              }
            }}
          >
            <ToolkitEditor
              content={q.question}
              onContentChange={(value) => handleUpdateQuestion(q.question_id, { question: value })}
              variableOptions={contextVariables}
              placeholder={t('dialog.auth.question_text_placeholder')}
              placeholderOnlyWhenEmpty
              maxLength={LENGTH_XL}
              size="md"
              aria-invalid={!!questionError}
              className="aria-invalid:border-warning"
            />
          </div>
          {questionError && <p className="text-xs text-warning">{questionError}</p>}
        </div>

        {/* Answer type */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t('dialog.auth.question_type_label')}</Label>
          <Select
            value={q.type}
            onValueChange={(val) =>
              handleUpdateQuestion(q.question_id, { type: val as AuthQuestionType })
            }
          >
            <SelectTrigger aria-invalid={!!typeError} className="aria-invalid:border-warning">
              <SelectValue placeholder={t('dialog.auth.question_type_placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {typeError && <p className="text-xs text-warning">{typeError}</p>}
        </div>

        {/* Save correct answer as */}
        <div className="space-y-1.5">
          <Autocomplete
            label={<Label className="mb-1 text-xs">{t('dialog.auth.save_as_label')}</Label>}
            options={saveAsOptions}
            value={addDollarSign(q.save_as)}
            onChange={(value) =>
              handleUpdateQuestion(q.question_id, { save_as: removeDollarSign(value) })
            }
            onCreateNew={(value) =>
              handleUpdateQuestion(q.question_id, {
                save_as: removeDollarSign(addDollarSign(value)),
              })
            }
            placeholder={t('dialog.auth.save_as_placeholder')}
            searchPlaceholder={t('dialog.auth.save_as_search_placeholder')}
          />
          {saveAsError && <p className="text-xs text-warning">{saveAsError}</p>}
        </div>

        {/* Weight */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t('dialog.auth.weight_label')}</Label>
          <AuthPointsSelector
            value={q.points}
            onChange={(val) => handleUpdateQuestion(q.question_id, { points: val })}
          />
          <p className="text-xs text-muted-foreground">{t('dialog.auth.weight_description')}</p>
        </div>

        {/* Validation method */}
        <div className="space-y-1.5">
          <Label className="text-xs">{t('dialog.auth.validation_method_label')}</Label>
          <ValidationMethodDropdown
            value={q.validation.method}
            webhookId={q.validation.webhook_id}
            webhooks={authValidationWebhooks}
            questionType={q.type}
            onChange={(method, webhookId) =>
              handleUpdateQuestion(q.question_id, {
                validation: buildValidationForMethod(q.validation, method, webhookId),
              })
            }
          />
        </div>

        {/* Validation guideline */}
        {q.validation.method === 'guideline' && (
          <div className="space-y-1.5">
            <Label className="text-xs">{t('dialog.auth.validation_guideline_label')}</Label>
            <Textarea
              value={q.validation.guideline ?? ''}
              onChange={(e) =>
                handleUpdateQuestion(q.question_id, {
                  validation: { ...q.validation, guideline: e.target.value },
                })
              }
              placeholder={t('dialog.auth.validation_guideline_placeholder')}
              rows={3}
              aria-invalid={!!validationError}
              className="aria-invalid:border-warning"
            />
            {validationError && <p className="text-xs text-warning">{validationError}</p>}
          </div>
        )}

        {/* Ground truth */}
        {(q.validation.method === 'exact_match' ||
          q.validation.method === 'fuzzy_match' ||
          q.validation.method === 'contains' ||
          q.validation.method === 'starts_with' ||
          q.validation.method === 'ends_with') && (
          <div className="space-y-1.5">
            <Autocomplete
              label={<Label className="mb-1 text-xs">{t('dialog.auth.ground_truth_label')}</Label>}
              options={saveAsOptions}
              value={addDollarSign(q.validation.ground_truth ?? '')}
              onChange={(value) =>
                handleUpdateQuestion(q.question_id, {
                  validation: { ...q.validation, ground_truth: addDollarSign(value) },
                })
              }
              onCreateNew={(value) =>
                handleUpdateQuestion(q.question_id, {
                  validation: { ...q.validation, ground_truth: addDollarSign(value) },
                })
              }
              placeholder={t('dialog.auth.save_as_placeholder')}
              searchPlaceholder={t('dialog.auth.save_as_search_placeholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('dialog.auth.ground_truth_description')}
            </p>
            {validationError && <p className="text-xs text-warning">{validationError}</p>}
          </div>
        )}

        {/* Fuzzy match threshold */}
        {q.validation.method === 'fuzzy_match' && (
          <div className="space-y-1.5">
            <Label className="text-xs">{t('dialog.auth.fuzzy_threshold_label')}</Label>
            <Input
              type="number"
              min={0}
              max={100}
              value={q.validation.fuzzy_match_threshold ?? 90}
              onChange={(e) =>
                handleUpdateQuestion(q.question_id, {
                  validation: {
                    ...q.validation,
                    fuzzy_match_threshold: (() => {
                      const parsed = parseInt(e.target.value, 10);
                      return isNaN(parsed) ? 90 : parsed;
                    })(),
                  },
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              {t('dialog.auth.fuzzy_threshold_description')}
            </p>
          </div>
        )}

        {/* Match size */}
        {PARTIAL_MATCH_METHODS.has(q.validation.method) && (
          <div className="space-y-1.5">
            <Label className="text-xs">{t('dialog.auth.match_size_label')}</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={q.validation.match_size ?? 3}
              onChange={(e) =>
                handleUpdateQuestion(q.question_id, {
                  validation: {
                    ...q.validation,
                    match_size: (() => {
                      const parsed = parseInt(e.target.value, 10);
                      return isNaN(parsed) ? 3 : parsed;
                    })(),
                  },
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              {t('dialog.auth.match_size_description')}
            </p>
          </div>
        )}
      </div>
    );
  };

  const templatePopoverContent = (
    <PopoverContent
      className="flex max-h-80 w-[var(--radix-popover-trigger-width)] min-w-64 flex-col p-0"
      align="end"
      sideOffset={4}
    >
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border px-3">
        <SearchIcon size={14} className="shrink-0 text-muted-foreground" />
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('dialog.auth.template_search_placeholder')}
          className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      <div className="space-y-3 overflow-y-auto py-3">
        {filteredTemplates.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dialog.auth.template_no_results')}</p>
        ) : (
          filteredTemplates.map((category) => (
            <div key={category.labelKey} className="space-y-1 px-1">
              <Label className="ml-2 text-xs text-muted-foreground">{t(category.labelKey)}</Label>
              {category.templates.map((tmpl) => {
                const alreadyAdded =
                  existingTemplateLabelKeys.has(tmpl.labelKey) &&
                  !removedTemplates.has(tmpl.labelKey);
                const isSelected = alreadyAdded || selectedTemplates.has(tmpl.labelKey);
                const pendingCount =
                  questions.length + selectedTemplates.size - removedTemplates.size;
                const isAtLimit = pendingCount >= MAX_QUESTIONS;
                const isDisabled = isAtLimit && !isSelected;
                const handleToggle = () => {
                  if (isDisabled) {
                    return;
                  }
                  if (alreadyAdded) {
                    setRemovedTemplates((prev) => {
                      const next = new Set(prev);
                      next.add(tmpl.labelKey);
                      return next;
                    });
                  } else if (removedTemplates.has(tmpl.labelKey)) {
                    setRemovedTemplates((prev) => {
                      const next = new Set(prev);
                      next.delete(tmpl.labelKey);
                      return next;
                    });
                  } else {
                    toggleTemplate(tmpl.labelKey);
                  }
                };
                return (
                  <label
                    key={tmpl.labelKey}
                    className={cn(
                      'flex h-8 items-center gap-2 rounded px-2',
                      isDisabled
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer hover:bg-muted/50'
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isDisabled}
                      onCheckedChange={handleToggle}
                    />
                    <span className="text-sm">{t(tmpl.shortLabelKey)}</span>
                  </label>
                );
              })}
            </div>
          ))
        )}

        {questions.length + selectedTemplates.size - removedTemplates.size >= MAX_QUESTIONS && (
          <p className="text-xs text-warning">{t('dialog.auth.template_max_reached')}</p>
        )}
      </div>
    </PopoverContent>
  );

  return (
    <AuthPagePanelWrapper title={stepTitle} onClose={onClose} canClose={false}>
      <div className="space-y-4">
        {!hasQuestions ? (
          <>
            <div className="flex flex-col items-center gap-1 pt-2">
              <img src="/assets/aiAgents/auth-questions-empty.png" alt="" className="h-24 w-24" />
              <h3 className="text-sm font-semibold">{t('dialog.auth.template_picker_title')}</h3>
              <p className="text-xs text-muted-foreground">
                {t('dialog.auth.template_picker_description')}
              </p>
            </div>

            {questionsError && <p className="text-xs text-warning">{questionsError}</p>}

            <div className="flex flex-col gap-2">
              <Popover open={templatePopoverOpen} onOpenChange={handleTemplatePopoverChange}>
                <PopoverTrigger asChild>
                  <Button className="w-full">
                    {t('dialog.auth.template_picker_choose')}
                    <ChevronDownIcon size={14} />
                  </Button>
                </PopoverTrigger>
                {templatePopoverContent}
              </Popover>

              <Button variant="ghost-neutral" className="w-full" onClick={handleAddCustomQuestion}>
                <PlusIcon size={14} />
                {t('dialog.auth.add_first_question')}
              </Button>
            </div>
          </>
        ) : (
          <>
            {questions.map((q, idx) => (
              <Fragment key={q.question_id}>
                {idx > 0 && <Separator />}
                {renderQuestionForm(q, idx)}
              </Fragment>
            ))}

            <Separator />

            <div className="flex gap-2">
              <Button
                variant="secondary-neutral"
                className="flex-1"
                size="sm"
                disabled={!canAddMore}
                onClick={handleAddCustomQuestion}
              >
                <PlusIcon size={14} />
                {t('dialog.auth.add_question')}
              </Button>
              <Popover open={templatePopoverOpen} onOpenChange={handleTemplatePopoverChange}>
                <InfoTooltip content={t('dialog.auth.template_picker_choose')}>
                  <PopoverTrigger asChild>
                    <Button
                      className="shrink-0"
                      variant="secondary-neutral"
                      size="icon-md"
                      disabled={!canAddMore}
                      aria-label={t('dialog.auth.template_picker_choose')}
                    >
                      <FolderIcon size={14} />
                    </Button>
                  </PopoverTrigger>
                </InfoTooltip>
                {templatePopoverContent}
              </Popover>
            </div>

            {!canAddMore && (
              <p className="text-xs text-muted-foreground">
                {t('dialog.auth.template_max_reached')}
              </p>
            )}

            {questionsError && <p className="text-xs text-warning">{questionsError}</p>}

            {/* Pre-enter webhook (verification step only) */}
            {step === 'verification' && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label className="text-sm font-semibold">
                      {t('dialog.auth.success_webhook_label')}
                    </Label>
                    <InfoTooltip content={t('dialog.auth.success_webhook_tooltip')}>
                      <InfoIcon size={14} className="text-muted-foreground" />
                    </InfoTooltip>
                  </div>
                  <Select
                    value={stepConfig?.pre_enter_webhook_id || NONE_VALUE}
                    onValueChange={(val) =>
                      updateStepConfig({
                        pre_enter_webhook_id: val === NONE_VALUE ? undefined : val,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('dialog.auth.success_webhook_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>
                        {t('dialog.auth.success_webhook_none')}
                      </SelectItem>
                      {authPreEnterWebhooks.map((wh) => (
                        <SelectItem key={wh.webhook_id} value={wh.webhook_id}>
                          {wh.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AuthPagePanelWrapper>
  );
};

export default AuthPageStepPanel;
